import { useLocation, useNavigate } from 'react-router-dom'

import { WISCreateModalQueryString } from './WISCreateModalQueryString'
import WISModalWrapper from './WISModalWrapper'

interface IWISCreateModalQueryStringHandlerProps {
  stateId: string | undefined
  assessmentId: string | undefined
}

const getSearchParamValue = (
  queryString: string,
  param: string
): string | null => {
  return new URLSearchParams(queryString).get(param)
}

/**
 * Detects WIS assessment modal specific query strings and control
 * open/close and inital data load state
 *
 * Currently utilized by:
 * - Current state "create state & scenario" redirect
 * - Saved state "run scenario"
 * - Saved state "rerun scenario"
 *
 * Nested under Saved State page as WIS works on saved state.
 */
const WISCreateModalQueryStringHandler = ({
  stateId,
  assessmentId
}: IWISCreateModalQueryStringHandlerProps) => {
  const navigate = useNavigate()
  const { pathname, search } = useLocation()
  const isOpen = getSearchParamValue(search, WISCreateModalQueryString.IsOpen)
  const isRerun = getSearchParamValue(search, WISCreateModalQueryString.IsRerun)

  const closeModal = (pathnameOverride?: string): void => {
    if (pathnameOverride != null && typeof pathnameOverride !== 'string') {
      // Ignore potential Event parameter when invoked without explicit parameter specified
      pathnameOverride = undefined
    }

    // If we are navigating to a different page, do NOT carry forward current search params.
    if (pathnameOverride) {
      navigate(pathnameOverride)

      return
    }

    const searchParams = new URLSearchParams(search)

    searchParams.delete(WISCreateModalQueryString.IsOpen)
    searchParams.delete(WISCreateModalQueryString.IsRerun)
    navigate(`${pathnameOverride ?? pathname}?${searchParams.toString()}`)
  }

  if (stateId == null) {
    return null
  }

  return (
    <WISModalWrapper
      stateId={stateId}
      assessmentId={assessmentId}
      isModalOpen={isOpen != null}
      isRerun={isRerun != null}
      closeModal={closeModal}
    />
  )
}

export default WISCreateModalQueryStringHandler
